// AGC: useDashboardData - Aggregates all data for the home dashboard
// AGC: Calculates projected income, costs, and gross margin from real lot data
// AGC: Fetches live commodity prices based on FARM GPS location (not device GPS)
// AGC: Fetches recent activities from notifications

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLotes } from '../context/LotesContext'
import { useFarms } from '../context/FarmContext'
import { useAuth } from './useAuth'
import { getExpenses } from '../services/expenses'
import { fetchRecentActivities, formatActivityTime } from '../services/activityFeedService'
import type { Activity } from '../services/activityFeedService'
import {
  getBatchPrices,
  getPriceForCrop,
  getDefaultYield,
  getDefaultCostPerHa,
  clearPriceCache,
  triggerScrapePrices,
  type CropPrices,
  type PriceReference,
} from '../services/priceService'
import type { Plot, CropType } from '../types/field'
import type { Expense } from '../types/financial'

// =============================================================================
// AGC: Types
// =============================================================================

/**
 * AGC: Financial projection for a single lot
 */
export interface LotProjection {
  lotId: string
  lotName: string
  cropType: string
  areaHa: number
  projectedYield: number // AGC: sacas/ha or tons/ha
  totalProduction: number // AGC: sacas or tons
  pricePerUnit: number // AGC: R$/saca or R$/ton
  projectedRevenue: number // AGC: R$
  projectedCost: number // AGC: R$
  projectedMargin: number // AGC: R$
}

/**
 * AGC: Dashboard financial summary
 */
export interface DashboardFinancials {
  projectedIncome: number // AGC: Total projected revenue in R$
  projectedCost: number // AGC: Total projected cost in R$
  grossMargin: number // AGC: projectedIncome - projectedCost
  executedExpenses: number // AGC: Actual expenses from Firebase
  remainingCost: number // AGC: projectedCost - executedExpenses
  totalAreaHa: number // AGC: Total planted area
  lotCount: number // AGC: Number of active lots
  lotProjections: LotProjection[] // AGC: Per-lot breakdown
}

/**
 * AGC: Price source information
 */
export interface PriceSource {
  cidade: string
  estado: string
  fonte: string
  distanciaKm: number
  locationSource: 'farm' | 'browser' | 'default'
}

/**
 * AGC: Activity item for recent activities
 */
export interface ActivityItem {
  id: string
  type: string
  title: string
  description?: string
  icon: string
  timestamp: string
}

/**
 * AGC: Dashboard data hook return type
 */
export interface UseDashboardDataReturn {
  // AGC: Financial data
  financials: DashboardFinancials
  // AGC: Price info
  prices: CropPrices
  priceSource: PriceSource | null
  /** AGC: ISO date/time of last price fetch (from batch response data_consulta) */
  priceUpdatedAt: string | null
  // AGC: Recent activities
  activities: ActivityItem[]
  // AGC: Loading states
  isLoading: boolean
  isLoadingPrices: boolean
  isLoadingActivities: boolean
  /** AGC: True while triggering scraper (fetch new prices to Firebase for all users) */
  isFetchingNewPrices: boolean
  // AGC: Error states
  error: string | null
  // AGC: Actions
  refresh: () => Promise<void>
  refreshPrices: () => Promise<void>
  /** AGC: Fetch new prices from AGROLINK and save to Firestore for all users */
  fetchNewPricesToFirebase: () => Promise<{ success: boolean; error?: string }>
}

// =============================================================================
// AGC: Default crops for price display (Top 10 Brazilian commodities)
// =============================================================================

const DEFAULT_CROPS_FOR_PRICES = [
  'soja',      // AGC: 1. Soja - #1 export
  'milho',     // AGC: 2. Milho
  'cafe',      // AGC: 3. Café - Brasil é líder mundial
  'algodao',   // AGC: 4. Algodão
  'cana',      // AGC: 5. Cana de açúcar
  'trigo',     // AGC: 6. Trigo
  'arroz',     // AGC: 7. Arroz
  'feijao',    // AGC: 8. Feijão
  'laranja',   // AGC: 9. Laranja (suco)
  'cacau',     // AGC: 10. Cacau
]

// =============================================================================
// AGC: Activity icon mapping
// =============================================================================

const ACTIVITY_ICONS: { [key: string]: string } = {
  field: '🗺️',
  task: '✅',
  financial: '💰',
  inventory: '📦',
  equipment: '🚜',
  crop: '🌱',
  worker: '👷',
  weather: '🌤️',
  system: '⚙️',
  default: '📋',
}

// =============================================================================
// AGC: Hook Implementation
// =============================================================================

export const useDashboardData = (): UseDashboardDataReturn => {
  const { plots, isLoading: lotsLoading } = useLotes()
  const { currentFarm } = useFarms()
  const { user, getAuthToken } = useAuth()

  // AGC: State
  const [prices, setPrices] = useState<CropPrices>({})
  const [priceSource, setPriceSource] = useState<PriceSource | null>(null)
  const [priceUpdatedAt, setPriceUpdatedAt] = useState<string | null>(null)
  const [activities, setActivities] = useState<ActivityItem[]>([])
  const [executedExpenses, setExecutedExpenses] = useState<number>(0)
  const [isLoadingPrices, setIsLoadingPrices] = useState(true)
  const [isLoadingActivities, setIsLoadingActivities] = useState(true)
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(true)
  const [isFetchingNewPrices, setIsFetchingNewPrices] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // AGC: Get unique crop types from active lots (for financial calculations)
  const uniqueCropTypes = useMemo(() => {
    const crops = new Set<string>()
    const activePlots = plots.filter((p) => !p.isArchived)
    activePlots.forEach((plot) => {
      if (plot.cropType) {
        crops.add(plot.cropType)
      }
    })
    return Array.from(crops)
  }, [plots])

  // AGC: Combine default crops with user's lot crops to always show all 10 + any custom crops
  const cropsToFetch = useMemo(() => {
    const allCrops = new Set<string>(DEFAULT_CROPS_FOR_PRICES)
    // AGC: Add any custom crops from user's lots that might not be in default list
    uniqueCropTypes.forEach(crop => allCrops.add(crop))
    return Array.from(allCrops)
  }, [uniqueCropTypes])

  // AGC: Fetch prices for all crop types using FARM GPS coordinates
  const fetchPrices = useCallback(
    async (forceRefresh = false) => {
      // AGC: Always fetch prices for default crops (no early return)

      try {
        setIsLoadingPrices(true)
        setError(null)

        // AGC: CRITICAL - Use farm coordinates, then browser geolocation, then default
        // AGC: Farm coordinates are stored as [lat, lng] tuple
        let lat: number
        let lon: number
        let locationSource: 'farm' | 'browser' | 'default' = 'default'

        // AGC: Check for valid farm coordinates (not [0,0] which is invalid/default)
        const hasValidFarmCoords = currentFarm?.coordinates && 
          currentFarm.coordinates.length === 2 &&
          (currentFarm.coordinates[0] !== 0 || currentFarm.coordinates[1] !== 0)
          
        if (hasValidFarmCoords) {
          // AGC: Use farm GPS coordinates for price lookup
          lat = currentFarm.coordinates[0]
          lon = currentFarm.coordinates[1]
          locationSource = 'farm'
          console.log(`📍 AGC: Using farm coordinates for prices: ${lat}, ${lon} (${currentFarm.name})`)
        } else {
          // AGC: Try nativeGeolocation as fallback (Capacitor on native, browser API on web)
          try {
            const { getCurrentPosition } = await import('../services/nativeGeolocation')
            const coords = await getCurrentPosition({
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 300000,
            })
            lat = coords.latitude
            lon = coords.longitude
            locationSource = 'browser'
            console.log(`📍 AGC: Using nativeGeolocation for prices: ${lat}, ${lon}`)
          } catch (geoError) {
            // AGC: Geolocation failed or denied, use default
            console.log('📍 AGC: Geolocation failed, using default location:', geoError)
            lat = -26.9906 // AGC: Default to Balneário Camboriú, SC
            lon = -48.6355
            console.log('📍 AGC: Using default location (Balneário Camboriú, SC)')
          }
        }
        const response = await getBatchPrices(cropsToFetch, lat, lon, forceRefresh)

        setPrices(response.precos)
        setPriceUpdatedAt(response.data_consulta || null)

        if (response.referencia) {
          setPriceSource({
            cidade: response.referencia.cidade,
            estado: response.referencia.estado,
            fonte: response.referencia.fonte,
            distanciaKm: response.referencia.distancia_km,
            locationSource: locationSource,
          })
        } else {
          setPriceSource(null)
        }

        // AGC: Log location source for debugging
        const sourceLabel = locationSource === 'farm' 
          ? currentFarm?.name 
          : locationSource === 'browser' 
            ? 'browser geolocation' 
            : 'default location'
        console.log(
          `💰 AGC: Dashboard prices loaded for ${Object.keys(response.precos).length} crops from ${sourceLabel}`
        )
      } catch (err) {
        console.error('❌ AGC: Error fetching dashboard prices:', err)
        setError('Erro ao carregar preços')
      } finally {
        setIsLoadingPrices(false)
      }
    },
    [cropsToFetch, currentFarm]
  )

  // AGC: Fetch real expenses from Firebase
  const fetchExpenses = useCallback(async () => {
    try {
      setIsLoadingExpenses(true)
      const token = await getAuthToken()
      const expenses = await getExpenses(token)
      const total = expenses.reduce((sum: number, expense: Expense) => sum + expense.value, 0)
      setExecutedExpenses(total)
      console.log(`💰 AGC: Loaded ${expenses.length} expenses, total: R$ ${total.toFixed(2)}`)
    } catch (err) {
      console.error('❌ AGC: Error fetching expenses:', err)
      setExecutedExpenses(0)
    } finally {
      setIsLoadingExpenses(false)
    }
  }, [getAuthToken])

  // AGC: Fetch recent activities from unified activity feed
  const fetchActivities = useCallback(async () => {
    try {
      setIsLoadingActivities(true)
      const token = await getAuthToken()
      
      // AGC: Fetch from new unified activity feed API
      const recentActivities = await fetchRecentActivities(token, { limit: 10 })

      // AGC: Map to ActivityItem format
      const mappedActivities: ActivityItem[] = recentActivities.map((a: Activity) => ({
        id: a.id,
        type: a.type || 'system',
        title: a.title,
        description: a.description,
        icon: a.icon || ACTIVITY_ICONS[a.type || 'default'] || ACTIVITY_ICONS.default,
        timestamp: typeof a.timestamp === 'string' ? a.timestamp : a.timestamp.toISOString(),
      }))

      setActivities(mappedActivities)
      console.log(`📋 AGC: Loaded ${mappedActivities.length} recent activities from feed`)
    } catch (err) {
      console.error('❌ AGC: Error fetching activities:', err)
      setActivities([])
    } finally {
      setIsLoadingActivities(false)
    }
  }, [getAuthToken])

  // AGC: Calculate financials from lots and prices
  const financials = useMemo((): DashboardFinancials => {
    const activePlots = plots.filter((p) => !p.isArchived)
    const lotProjections: LotProjection[] = []

    let totalProjectedIncome = 0
    let totalProjectedCost = 0
    let totalAreaHa = 0

    for (const plot of activePlots) {
      const cropType = plot.cropType || 'outros'
      const areaHa = plot.area || 0

      // AGC: Get yield (use lot-specific if available, otherwise default)
      const yieldPerHa =
        plot.yieldEstimate && plot.yieldEstimate > 0
          ? plot.yieldEstimate / 60 // AGC: Convert kg/ha to sacas/ha (60kg/saca)
          : plot.financial?.projectedYield || getDefaultYield(cropType)

      // AGC: Get price (from live prices or fallback)
      const pricePerUnit = getPriceForCrop(cropType, prices, 100)

      // AGC: Get cost per ha (use lot-specific if available, otherwise default)
      const costPerHa =
        plot.financial?.projectedCost && areaHa > 0
          ? plot.financial.projectedCost / areaHa
          : getDefaultCostPerHa(cropType)

      // AGC: Calculate projections
      const totalProduction = yieldPerHa * areaHa
      const projectedRevenue = totalProduction * pricePerUnit
      const projectedCost = costPerHa * areaHa
      const projectedMargin = projectedRevenue - projectedCost

      lotProjections.push({
        lotId: plot.id,
        lotName: plot.name,
        cropType,
        areaHa,
        projectedYield: yieldPerHa,
        totalProduction,
        pricePerUnit,
        projectedRevenue,
        projectedCost,
        projectedMargin,
      })

      totalProjectedIncome += projectedRevenue
      totalProjectedCost += projectedCost
      totalAreaHa += areaHa
    }

    const grossMargin = totalProjectedIncome - totalProjectedCost
    const remainingCost = Math.max(0, totalProjectedCost - executedExpenses)

    return {
      projectedIncome: totalProjectedIncome,
      projectedCost: totalProjectedCost,
      grossMargin,
      executedExpenses,
      remainingCost,
      totalAreaHa,
      lotCount: activePlots.length,
      lotProjections,
    }
  }, [plots, prices, executedExpenses])

  // AGC: Refresh all data
  const refresh = useCallback(async () => {
    setError(null)
    clearPriceCache()
    await Promise.all([fetchPrices(true), fetchExpenses(), fetchActivities()])
  }, [fetchPrices, fetchExpenses, fetchActivities])

  // AGC: Refresh only prices
  const refreshPrices = useCallback(async () => {
    clearPriceCache()
    await fetchPrices(true)
  }, [fetchPrices])

  // AGC: Fetch new prices from AGROLINK and save to Firestore for all users
  const fetchNewPricesToFirebase = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    const token = await getAuthToken()
    if (!token) {
      return { success: false, error: 'Not authenticated' }
    }
    setIsFetchingNewPrices(true)
    try {
      const result = await triggerScrapePrices(token)
      if (result.success) {
        clearPriceCache()
        await fetchPrices(true)
      }
      return result
    } finally {
      setIsFetchingNewPrices(false)
    }
  }, [getAuthToken, fetchPrices])

  // AGC: Initial data fetch
  useEffect(() => {
    if (user?.uid) {
      fetchExpenses()
      fetchActivities()
    }
  }, [user?.uid, fetchExpenses, fetchActivities])

  // AGC: Fetch prices for all 10 default crops + any custom crops from lots
  useEffect(() => {
    // AGC: Always fetch prices since we have 10 default crops
    fetchPrices(false)
  }, [cropsToFetch, fetchPrices])

  // AGC: Overall loading state
  const isLoading = lotsLoading || isLoadingPrices || isLoadingExpenses

  return {
    financials,
    prices,
    priceSource,
    priceUpdatedAt,
    activities,
    isLoading,
    isLoadingPrices,
    isLoadingActivities,
    isFetchingNewPrices,
    error,
    refresh,
    refreshPrices,
    fetchNewPricesToFirebase,
  }
}

export default useDashboardData

